import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, FilePdf } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SITES = [
  { k: 'tricipital', l: 'Tricipital' }, { k: 'bicipital', l: 'Bicipital' },
  { k: 'subescapular', l: 'Subescapular' }, { k: 'suprailiaca', l: 'Suprailíaca' },
  { k: 'abdominal', l: 'Abdominal' }, { k: 'coxa', l: 'Coxa' }, { k: 'geminal', l: 'Geminal' },
];
const EMPTY_FORM = { date: new Date().toISOString().split('T')[0], sex: 'F', override_equation: '', ...SITES.reduce((a, s) => ({ ...a, [s.k]: '' }), {}) };

export default function SkinfoldsSection({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchAll = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/skinfolds`); setList(Array.isArray(data) ? data : []); } catch {}
  }, [patientId]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const open = (m) => {
    setEditing(m);
    setForm(m ? { date: m.date, sex: m.sex || 'F', override_equation: '', ...SITES.reduce((a, s) => ({ ...a, [s.k]: m[s.k] ?? '' }), {}) } : EMPTY_FORM);
    setShowForm(true);
  };
  const save = async () => {
    const payload = { date: form.date, sex: form.sex, override_equation: form.override_equation || undefined, ...SITES.reduce((a, s) => ({ ...a, [s.k]: form[s.k] === '' ? null : parseFloat(form[s.k]) }), {}) };
    try {
      if (editing?.id) await axios.put(`${API}/api/skinfolds/${editing.id}`, payload);
      else await axios.post(`${API}/api/patients/${patientId}/skinfolds`, payload);
      toast.success('Registo guardado'); setShowForm(false); setEditing(null); fetchAll();
    } catch { toast.error('Erro ao guardar'); }
  };
  const remove = async (id) => {
    const rec = list.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o registo de pregas cutâneas de ${rec?.date || ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/skinfolds/${id}`); toast.success('Eliminado'); fetchAll(); } catch { toast.error('Erro'); }
  };

  const latest = list[0];

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="skinfolds-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <p className="text-xs font-medium text-brand-text">Pregas Cutâneas</p>
          {latest?.body_fat_pct != null && <p className="text-[10px] text-brand-muted mt-0.5">Última estimativa: <span className="font-semibold text-brand-green">{latest.body_fat_pct}% massa gorda</span> ({latest.equation_used === 'slaughter' ? 'Slaughter' : 'Durnin-Womersley'})</p>}
          {latest?.clinical_note && <p className="text-[10px] text-brand-muted mt-0.5 italic">{latest.clinical_note}</p>}
        </div>
        <div className="flex gap-2">
          {list.length > 0 && <button onClick={() => window.open(`${API}/api/patients/${patientId}/skinfolds/pdf`, '_blank')} className="text-xs text-brand-green flex items-center gap-1" data-testid="skinfolds-pdf-btn"><FilePdf size={12} /> PDF</button>}
          <button onClick={() => open(null)} className="text-xs text-brand-green flex items-center gap-1" data-testid="add-skinfold-btn"><Plus size={12} /> Nova avaliação</button>
        </div>
      </div>
      {list.length === 0 ? (
        <p className="text-xs text-brand-placeholder italic">Sem pregas cutâneas registadas.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-brand-muted border-b border-brand-border">
                <th className="py-1.5 pr-2">Data</th>
                {SITES.map(s => <th key={s.k} className="py-1.5 px-1 text-right">{s.l}</th>)}
                <th className="py-1.5 px-1 text-right">% MG</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map(m => (
                <tr key={m.id} className="border-b border-brand-border/50 last:border-0" data-testid={`skinfold-row-${m.id}`}>
                  <td className="py-1.5 pr-2 font-medium">{fmtDatePt(m.date)}</td>
                  {SITES.map(s => <td key={s.k} className="py-1.5 px-1 text-right text-brand-muted">{m[s.k] ?? '-'}</td>)}
                  <td className="py-1.5 px-1 text-right font-medium text-brand-green">{m.body_fat_pct ?? '-'}</td>
                  <td className="py-1.5 pl-2 flex gap-1">
                    <button onClick={() => open(m)} className="text-brand-muted hover:text-brand-green p-1" data-testid={`edit-skinfold-${m.id}`}><PencilSimple size={12} /></button>
                    <button onClick={() => remove(m.id)} className="text-brand-muted hover:text-red-500 p-1" data-testid={`del-skinfold-${m.id}`}><Trash size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editing ? 'Editar avaliação' : 'Nova avaliação de pregas cutâneas'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="skinfold-date" /></div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Sexo</Label>
                <select value={form.sex} onChange={e => setForm({ ...form, sex: e.target.value })} className="w-full text-sm border border-brand-border rounded-xl px-3 py-2 bg-white" data-testid="skinfold-sex">
                  <option value="F">Feminino</option><option value="M">Masculino</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {SITES.map(s => (
                <div key={s.k}>
                  <Label className="text-[10px] text-brand-muted block">{s.l} (mm)</Label>
                  <Input type="number" step="0.1" min="0" value={form[s.k]} onChange={e => setForm({ ...form, [s.k]: e.target.value })} placeholder="-" className="rounded-xl border-brand-border text-sm h-8" data-testid={`skinfold-input-${s.k}`} />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Equação (automática por defeito, idade/sexo/gravidez)</Label>
              <select value={form.override_equation} onChange={e => setForm({ ...form, override_equation: e.target.value })} className="w-full text-sm border border-brand-border rounded-xl px-3 py-2 bg-white" data-testid="skinfold-equation-override">
                <option value="">Automática</option>
                <option value="durnin_womersley">Durnin-Womersley (adulto)</option>
                <option value="slaughter">Slaughter (criança/adolescente)</option>
              </select>
            </div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium" data-testid="save-skinfold-btn">{editing ? 'Guardar alterações' : 'Guardar avaliação'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
